import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

// Per-root cache of (photoId → sha256) so a re-scan of the same root only
// hashes photos we haven't seen before. Keyed by the photo's encryptedMeta
// fingerprint *too*: if a future feature mutates the meta (rotation already
// re-wraps the cipher under a new key, so its sha256 of the plaintext stays
// the same — but if we ever rewrite content, this guard prevents stale hits).
//
// The hash of the *plaintext* lives client-side only. Sending it to the
// server would create an oracle for content guessing. The store is on the
// device, behind the user's session.

const DB_NAME = 'photo-vault-dedup';
const DB_VERSION = 1;
const STORE = 'photoHashes';

interface HashEntry {
  // Composite key `${rootId}::${photoId}` — IndexedDB doesn't compose
  // primitives natively without a compound key store, and keeping it as a
  // string is simpler than maintaining the schema across migrations.
  key: string;
  rootId: string;
  photoId: string;
  size: number;
  // Bound the cached sha256 to the cipher version the worker decrypted. If
  // the encryptedMeta changes (rotation, future re-encryption) we treat the
  // cached row as stale and recompute. We don't hash the cipher itself
  // (would defeat the purpose of caching) — the encryptedMeta acts as a
  // cheap version tag.
  metaTag: string;
  sha256: string;
  hashedAt: number;
}

interface DedupSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: HashEntry;
    indexes: { byRoot: string };
  };
}

let dbPromise: Promise<IDBPDatabase<DedupSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<DedupSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<DedupSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const store = db.createObjectStore(STORE, { keyPath: 'key' });
          store.createIndex('byRoot', 'rootId');
        }
      },
    });
  }
  return dbPromise;
}

function compositeKey(rootId: string, photoId: string): string {
  return `${rootId}::${photoId}`;
}

// Cheap fingerprint of the encryptedMeta: first 32 chars are enough to
// detect rewrites without storing the whole base64url string for thousands
// of photos.
function tagFor(encryptedMeta: string): string {
  return encryptedMeta.slice(0, 32);
}

export interface CachedHash {
  sha256: string;
  size: number;
}

export async function getCachedHash(
  rootId: string,
  photoId: string,
  encryptedMeta: string,
  size: number,
): Promise<CachedHash | null> {
  try {
    const db = await getDb();
    const entry = await db.get(STORE, compositeKey(rootId, photoId));
    if (!entry) return null;
    if (entry.size !== size) return null;
    if (entry.metaTag !== tagFor(encryptedMeta)) return null;
    return { sha256: entry.sha256, size: entry.size };
  } catch {
    return null;
  }
}

export async function putCachedHash(
  rootId: string,
  photoId: string,
  encryptedMeta: string,
  size: number,
  sha256: string,
): Promise<void> {
  try {
    const db = await getDb();
    await db.put(STORE, {
      key: compositeKey(rootId, photoId),
      rootId,
      photoId,
      size,
      metaTag: tagFor(encryptedMeta),
      sha256,
      hashedAt: Date.now(),
    });
  } catch {
    // best-effort cache
  }
}

// Drop every cached hash under a root. Called when the user explicitly
// requests a fresh scan (we don't expose this in the UI yet, but it's the
// natural escape hatch if the cache ever drifts).
export async function clearRootCache(rootId: string): Promise<void> {
  try {
    const db = await getDb();
    const tx = db.transaction(STORE, 'readwrite');
    const index = tx.store.index('byRoot');
    let cursor = await index.openCursor(rootId);
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  } catch {
    // ignore
  }
}
